// Copyright (c) Ping. All rights reserved.

import type React from 'react';

import {
  type StagedAttachment,
  type ThreadComposerProps,
  type ThreadGateway,
  ThreadPanelOverlay,
  type ThreadRowActions,
} from '@ping/ui';

import type { ChatHostProps } from './types';

/**
 * invite-flow Wave 2 — web orchestrator for the shared
 * `ThreadPanelOverlay`.
 *
 * The host forwards the web's `ThreadGateway` (typically derived from
 * the session store's `MessagingClient`) into the shared overlay,
 * which owns the inner thread VM lifecycle and the close escalation
 * back into the shell view-model. `composerProps` (thread-scoped
 * attachment / voice / GIF / sticker / poll / games / location senders)
 * and `rowActions` (the per-reply message-actions popover) are wired by
 * the route host and forwarded straight through.
 */
export interface ThreadPanelHostProps extends ChatHostProps {
  readonly gateway: ThreadGateway;
  readonly composerProps?: ThreadComposerProps | undefined;
  readonly rowActions?: ThreadRowActions | undefined;
  readonly stagedAttachments?: readonly StagedAttachment[] | undefined;
  readonly onRemoveStagedAttachment?: ((id: string) => void) | undefined;
  readonly onFlushStagedAttachments?: (() => Promise<void> | void) | undefined;
  /** Render in-flow inside the host's resizable right-panel slot. */
  readonly embedded?: boolean | undefined;
}

export const ThreadPanelHost: React.FC<ThreadPanelHostProps> = ({
  vm,
  gateway,
  composerProps,
  rowActions,
  stagedAttachments,
  onRemoveStagedAttachment,
  onFlushStagedAttachments,
  embedded,
}) => (
  <ThreadPanelOverlay
    vm={vm}
    gateway={gateway}
    {...(embedded !== undefined ? { embedded } : {})}
    {...(composerProps !== undefined ? { composerProps } : {})}
    {...(rowActions !== undefined ? { rowActions } : {})}
    {...(stagedAttachments !== undefined ? { stagedAttachments } : {})}
    {...(onRemoveStagedAttachment !== undefined ? { onRemoveStagedAttachment } : {})}
    {...(onFlushStagedAttachments !== undefined ? { onFlushStagedAttachments } : {})}
  />
);
